import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import RegistrationHeader from "./components/RegistrationHeader";
import RegistrationForm from "./components/RegistrationForm";
import OAuthOptions from "./components/OAuthOptions";
import PrivacyConsent from "./components/PrivacyConsent";
import AuthenticationGuard from "../../components/ui/AuthenticationGuard";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import SmartLoadingBuffer from "../../components/ui/SmartLoadingBuffer";
import useAuth from "../../hooks/useAuth";

const RegisterPage = () => {
  const navigate = useNavigate();
  const {
    register: registerUser,
    loginWithProvider,
    authPending,
    pendingVerificationEmail,
    resendConfirmationEmail,
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Form, 2: Consent, 3: Verify Email
  const [formData, setFormData] = useState(null);
  const [consents, setConsents] = useState({});
  const [consentValid, setConsentValid] = useState(false);
  const [consentError, setConsentError] = useState("");
  const [verificationEmail, setVerificationEmail] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState({
    success: false,
    error: null,
  });

  const handleNavigateToLogin = () => {
    navigate("/login");
  };

  const handleFormSubmit = (data) => {
    setFormData(data);
    setStep(2);
  };

  const handleOAuthSignup = async (provider) => {
    try {
      await loginWithProvider(provider);
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error(`${provider} OAuth error:`, error);
      setConsentError(`${provider} authentication failed. Please try again.`);
    }
  };

  const handleConsentChange = (newConsents, isValid) => {
    setConsents(newConsents);
    setConsentValid(isValid);
    setConsentError(
      isValid ? "" : "Please accept the required terms to continue",
    );
  };

  const handleFinalRegistration = async () => {
    if (!consentValid) {
      setConsentError(
        "Please accept the required terms and privacy policy to continue",
      );
      return;
    }

    setLoading(true);

    try {
      const result = await registerUser(formData, { consents });
      if (result?.emailConfirmationRequired) {
        const emailToVerify = result?.user?.email || formData?.email;
        setVerificationEmail(emailToVerify);
        setResendStatus({ success: false, error: null });
        setStep(3);
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      setConsentError(
        error?.message || "Registration failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForm = () => {
    setStep(1);
    setConsentError("");
  };

  const handleResendConfirmation = async () => {
    const email =
      verificationEmail || pendingVerificationEmail || formData?.email;
    if (!email) {
      setResendStatus({
        success: false,
        error: "Email address unavailable. Please try registering again.",
      });
      return;
    }

    setResendLoading(true);
    setResendStatus({ success: false, error: null });

    try {
      await resendConfirmationEmail(email);
      setResendStatus({ success: true, error: null });
    } catch (error) {
      setResendStatus({
        success: false,
        error: error?.message || "Failed to resend confirmation email.",
      });
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    if (pendingVerificationEmail && step !== 3) {
      setVerificationEmail(pendingVerificationEmail);
      setStep(3);
    }
  }, [pendingVerificationEmail, step]);

  const isProcessing = loading || authPending;
  const verificationTargetEmail =
    verificationEmail || pendingVerificationEmail || formData?.email;

  const registerHighlights = useMemo(
    () => [
      {
        icon: "CalendarCheck",
        title: "Guided rituals",
        description:
          "Set up habit cadences that align with your learning science goals from day one.",
      },
      {
        icon: "Library",
        title: "Knowledge hub",
        description:
          "Capture notes, flashcards, and habits in a single knowledge base built for recall.",
      },
      {
        icon: "BarChart3",
        title: "Progress analytics",
        description:
          "See streaks, completion rates, and insights across every dimension of your learning.",
      },
    ],
    [],
  );

  if (authPending && step !== 3) {
    return (
      <AuthenticationGuard requireAuth={false}>
        <div className="min-h-screen bg-background">
          <SmartLoadingBuffer
            className="min-h-screen"
            title="Setting up your workspace"
            message="We’re stitching together your profile, preferences, and starter insights."
            hint="This should only take a moment."
          />
        </div>
      </AuthenticationGuard>
    );
  }

  return (
    <AuthenticationGuard requireAuth={false} fallbackRedirect="/dashboard">
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-2 min-h-screen">
          {/* Left column */}
          <div className="relative hidden lg:flex flex-col justify-between bg-gradient-to-br from-primary/15 via-background to-background p-12">
            <RegistrationHeader onNavigateToLogin={handleNavigateToLogin} />
            <div className="bg-card/60 backdrop-blur border border-border/60 rounded-xl p-6 space-y-4 mt-10">
              <h2 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                <Icon name="NotebookPen" size={18} className="text-primary" />
                <span>What happens after you join</span>
              </h2>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start space-x-2">
                  <Icon name="Compass" size={16} className="mt-0.5 text-primary" />
                  <span>Receive an onboarding path tailored to your goals and study cadence.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Sparkles" size={16} className="mt-0.5 text-accent" />
                  <span>Unlock AI briefings that summarize key insights after each study sprint.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="FileBarChart" size={16} className="mt-0.5 text-success" />
                  <span>Track learning momentum with trendlines across habits, flashcards, and notes.</span>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-1 gap-4 mt-6">
              {registerHighlights.map((highlight) => (
                <div
                  key={highlight.title}
                  className="h-full rounded-xl border border-border/70 bg-card/60 p-4 space-y-2 text-left shadow-subtle"
                >
                  <div className="flex items-center space-x-2 text-sm font-medium text-foreground">
                    <Icon name={highlight.icon} size={16} className="text-primary" />
                    <span>{highlight.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug">
                    {highlight.description}
                  </p>
                </div>
              ))}
            </div>
            <div className="text-xs text-muted-foreground/80">
              <p>&copy; {new Date().getFullYear()} Cogito. Built for focused, evidence-based learning.</p>
            </div>
          </div>

          {/* Right column */}
          <div className="flex flex-col justify-center px-4 py-10 sm:px-8 lg:px-16 bg-background">
            <div className="w-full max-w-xl mx-auto space-y-8">
              <div className="lg:hidden">
                <RegistrationHeader onNavigateToLogin={handleNavigateToLogin} />
              </div>

              {step === 1 && (
                <div className="space-y-8">
                  <div className="bg-card rounded-2xl border border-border/80 shadow-subtle p-6 sm:p-8 space-y-6">
                    <div className="space-y-2">
                      <h1 className="text-2xl font-semibold text-foreground">Create your account</h1>
                      <p className="text-sm text-muted-foreground">
                        You’re moments away from a smarter, AI-guided study routine.
                      </p>
                    </div>

                    <RegistrationForm onSubmit={handleFormSubmit} loading={isProcessing} />

                    <OAuthOptions onOAuthSignup={handleOAuthSignup} loading={isProcessing} />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:hidden">
                    {registerHighlights.map((highlight) => (
                      <div
                        key={highlight.title}
                        className="h-full rounded-xl border border-border/70 bg-card/60 p-4 space-y-2 text-left shadow-subtle"
                      >
                        <div className="flex items-center space-x-2 text-sm font-medium text-foreground">
                          <Icon name={highlight.icon} size={16} className="text-primary" />
                          <span>{highlight.title}</span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-snug">
                          {highlight.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-8">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={handleBackToForm}
                      className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
                      disabled={isProcessing}
                    >
                      <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center">
                        <div className="w-4 h-4 border-l-2 border-b-2 border-current transform rotate-45"></div>
                      </div>
                      <span className="text-sm font-medium">Back to form</span>
                    </button>
                    <div className="flex-1">
                      <h1 className="text-2xl font-bold text-foreground">Privacy & consent</h1>
                      <p className="text-sm text-muted-foreground">
                        Review our terms to complete your registration.
                      </p>
                    </div>
                  </div>

                  <div className="bg-card rounded-2xl border border-border/80 shadow-subtle p-6 sm:p-8 space-y-6">
                    <PrivacyConsent onConsentChange={handleConsentChange} error={consentError} />
                    <div className="pt-4">
                      <Button
                        fullWidth
                        size="lg"
                        onClick={handleFinalRegistration}
                        disabled={!consentValid || isProcessing}
                        loading={isProcessing}
                        iconName="ShieldCheck"
                      >
                        Complete registration
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-8">
                  <div className="bg-card rounded-2xl border border-border/80 shadow-subtle p-6 sm:p-8 space-y-6">
                    <div className="flex items-start space-x-3">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="Mail" size={24} className="text-primary" />
                      </div>
                      <div className="space-y-3">
                        <div>
                          <h1 className="text-2xl font-bold text-foreground mb-1">
                            Confirm your email to finish signing up
                          </h1>
                          <p className="text-sm text-muted-foreground">
                            We sent a verification link to
                            <span className="font-medium text-foreground">
                              {" "}
                              {verificationTargetEmail || "your email address"}
                            </span>
                            . Please confirm your email to activate your account.
                          </p>
                        </div>

                        <div className="space-y-3">
                          <p className="text-sm text-muted-foreground">
                            Didn’t receive the email? Check your spam folder or resend the confirmation message below.
                          </p>

                          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                            <Button
                              variant="outline"
                              onClick={handleResendConfirmation}
                              disabled={resendLoading || authPending}
                              loading={resendLoading}
                              iconName="RefreshCw"
                            >
                              Resend confirmation email
                            </Button>
                            <Button onClick={handleNavigateToLogin} iconName="LogIn" variant="secondary">
                              Go to sign in
                            </Button>
                          </div>

                          {resendStatus.success && (
                            <div className="text-sm text-success">
                              A new confirmation email has been sent. It may take a couple of minutes to arrive.
                            </div>
                          )}

                          {resendStatus.error && (
                            <div className="text-sm text-error">
                              {resendStatus.error}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-muted/50 border border-dashed border-border rounded-xl p-6">
                    <h2 className="text-sm font-semibold text-foreground mb-2">Tips</h2>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Add support@supabase.io to your contacts to ensure delivery.</li>
                      <li>• If you no longer have access to the email above, register again with the correct address.</li>
                    </ul>
                  </div>
                </div>
              )}

              <div className="text-center text-xs text-muted-foreground">
                <p>
                  By creating an account, you're joining a community of learners committed to evidence-based personal development. Welcome to Cogito!
                </p>
                <p className="mt-2">
                  © {new Date()?.getFullYear()} Cogito. Built with privacy and learning science in mind.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthenticationGuard>
  );
}

export default RegisterPage;
